/**
 * @element qds-radio-group
 *
 * Mutually exclusive choice from a small set of options (2–5). Above 5
 * options, use `qds-select`.
 *
 * The group owns everything; `qds-radio` is a presentational leaf. The group
 * may be driven by slotted `<qds-radio>` children or by the `options` property
 * (which accepts the same `QdsOption[]` shape as `qds-select`). When both are
 * present, property options win and slotted children are not rendered.
 *
 * Arrow keys SELECT ON MOVE — the ARIA radio-group pattern. This differs from
 * `qds-select`, where arrows only move an active option without selecting it.
 * The difference is intentional: radio groups show all options at once, and
 * screen reader users expect arrow navigation to immediately select.
 *
 * @attr {string} value - The selected radio's value.
 * @attr {string} label - Group label text.
 * @attr {string} hint - Helper text below the options.
 * @attr {string} error-text - Error message shown when `invalid` is true.
 * @attr {string} name - Form field name.
 * @attr {'vertical'|'horizontal'} orientation - Layout direction. Default "vertical".
 * @attr {'sm'|'md'|'lg'} size - Control size for all child radios. Default "md".
 * @attr {boolean} disabled - Disables the entire group; cascades to all radios.
 * @attr {boolean} readonly - Blocks selection without dimming.
 * @attr {boolean} required - At least one radio must be selected.
 * @attr {boolean} invalid - Author-forced invalid state.
 *
 * @csspart container - The outer wrapper.
 * @csspart label - The group label.
 * @csspart required - The required marker span.
 * @csspart options - The options container.
 * @csspart hint - The helper text.
 * @csspart error - The error message.
 *
 * @fires change - When selection changes. Detail: `{ component, value }`.
 * @fires input - Same moment as change. Detail: `{ component, value }`.
 * @fires qds-validity-change - When validity transitions. Detail: `QdsValidityChangeEventDetail`.
 *
 * @example
 * <qds-radio-group name="basis" label="Cost basis" value="fifo" required>
 *   <qds-radio value="fifo" label="First in, first out"></qds-radio>
 *   <qds-radio value="lifo" label="Last in, first out"></qds-radio>
 *   <qds-radio value="avg" label="Average cost" hint="Weighted by quantity"></qds-radio>
 * </qds-radio-group>
 */

/**
 * @element qds-radio
 *
 * Presentational leaf for `qds-radio-group`. Not form-associated; the group
 * owns form participation. Authors should not set `checked` directly — the
 * group manages it.
 *
 * @attr {string} value - The value this radio represents.
 * @attr {string} label - Label text. Also settable as slotted text content.
 * @attr {string} hint - Per-option helper text.
 * @attr {boolean} disabled - Disables this radio. Cannot un-disable inside a disabled group.
 * @attr {boolean} checked - Set by the group, not by authors.
 *
 * @csspart row - The clickable row.
 * @csspart dot - The radio circle.
 * @csspart dot-inner - The inner dot of a selected radio.
 * @csspart label - The label text.
 * @csspart hint - The per-option helper text.
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseSize,
  upgradeProperty,
} from '../shared/form-control.js';
import type {
  QdsControlSize,
  QdsOption,
  QdsValidity,
  QdsValidityChangeEventDetail,
} from '../../types/index';

interface RadioGroupChangeEventDetail {
  component: HTMLElement;
  value: string;
}

// ── QdsRadio ───────────────────────────────────────────────────────────

export class QdsRadio extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['value', 'label', 'hint', 'disabled', 'checked'];
  }

  private _value: string = '';
  private _label: string = '';
  private _hint: string = '';
  private _disabled: boolean = false;
  private _checked: boolean = false;

  private _clickHandler: ((e: MouseEvent) => void) | null = null;
  private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  // Properties

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    this._value = String(value);
    this.setAttribute('value', this._value);
    if (this.isConnected) this.render();
  }

  get label(): string {
    return this._label;
  }

  set label(value: string) {
    this._label = String(value);
    this.setAttribute('label', this._label);
    if (this.isConnected) this.render();
  }

  get hint(): string {
    return this._hint;
  }

  set hint(value: string) {
    this._hint = String(value);
    this.setAttribute('hint', this._hint);
    if (this.isConnected) this.render();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = Boolean(value);
    reflectBoolean(this, 'disabled', this._disabled);
    if (this.isConnected) this.render();
  }

  get checked(): boolean {
    return this._checked;
  }

  set checked(value: boolean) {
    this._checked = Boolean(value);
    reflectBoolean(this, 'checked', this._checked);
    if (this.isConnected) this.render();
  }

  // Lifecycle

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._value = this.getAttribute('value') || '';
    this._label = this.getAttribute('label') || '';
    this._hint = this.getAttribute('hint') || '';
    this._disabled = isAttrTruthy(this.getAttribute('disabled'));
    this._checked = isAttrTruthy(this.getAttribute('checked'));
    this.render();
  }

  disconnectedCallback(): void {
    this._removeListeners();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value':
        this._value = newValue || '';
        break;
      case 'label':
        this._label = newValue || '';
        break;
      case 'hint':
        this._hint = newValue || '';
        break;
      case 'disabled':
        this._disabled = newValue !== null && newValue !== 'false';
        break;
      case 'checked':
        this._checked = newValue !== null && newValue !== 'false';
        break;
    }

    if (this.isConnected) this.render();
  }

  // Internals

  private _removeListeners(): void {
    if (this._clickHandler) {
      this.removeEventListener('click', this._clickHandler);
      this._clickHandler = null;
    }
    if (this._keydownHandler) {
      this.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }
  }

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this._removeListeners();

    // The group controls size; read from the group's size attribute or default md.
    const group = this.closest('qds-radio-group');
    const groupSize = group ? parseSize(group.getAttribute('size')) : 'md';

    // Effective disabled: group disabled cascades, radio can only add, not remove.
    const groupDisabled = group ? isAttrTruthy(group.getAttribute('disabled')) : false;
    const effectiveDisabled = groupDisabled || this._disabled;
    const groupReadonly = group ? isAttrTruthy(group.getAttribute('readonly')) : false;
    const blocked = effectiveDisabled || groupReadonly;

    const dotSize =
      groupSize === 'sm' ? '16px'
      : groupSize === 'lg' ? '20px'
      : '18px';

    const fontSize =
      groupSize === 'sm' ? '14px'
      : groupSize === 'lg' ? '16px'
      : '15px';

    const rowMinHeight =
      groupSize === 'sm' ? '28px'
      : groupSize === 'lg' ? '36px'
      : '32px';

    const tabIndex = this._checked ? '0' : '-1';
    const useSlotForLabel = this._label === '' && this.textContent?.trim().length;
    const labelText = this._label !== '' ? this._escapeHtml(this._label) : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333);
          font-family: inherit;
          font-size: ${fontSize};
          outline: none;
        }

        :host([disabled]) {
          opacity: 0.55;
          cursor: not-allowed;
          pointer-events: none;
        }

        :host(:focus-visible) .dot {
          box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
        }

        .row {
          display: flex;
          align-items: flex-start;
          gap: var(--theme-space-2, 8px);
          min-height: ${rowMinHeight};
          padding: 2px 0;
          box-sizing: border-box;
          cursor: pointer;
          border-radius: var(--theme-radius-sm, 4px);
          transition: background-color var(--transition-speed-fast, 0.15s);
        }

        .row:hover {
          background-color: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
        }

        :host([disabled]) .row,
        :host([data-blocked]) .row {
          cursor: default;
        }

        :host([disabled]) .row:hover,
        :host([data-blocked]) .row:hover {
          background-color: transparent;
        }

        .dot {
          flex-shrink: 0;
          width: ${dotSize};
          height: ${dotSize};
          min-width: ${dotSize};
          border: 1.5px solid var(--theme-border-secondary, #C5CEE0);
          border-radius: 50%;
          background: var(--theme-background-primary, #fff);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 1px;
          box-sizing: border-box;
          transition: border-color var(--transition-speed-fast, 0.15s),
                      background-color var(--transition-speed-fast, 0.15s),
                      box-shadow var(--transition-speed-fast, 0.15s);
        }

        .row:hover .dot {
          border-color: var(--theme-border-strong, #AFBBD1);
        }

        :host([disabled]) .row:hover .dot {
          border-color: var(--theme-border-secondary, #C5CEE0);
        }

        .dot.selected {
          border-color: var(--theme-accent-primary, #445E89);
        }

        .dot-inner {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--theme-accent-primary, #445E89);
        }

        .label {
          color: var(--theme-text-primary, #333);
          line-height: 1.4;
          padding-top: 1px;
        }

        .hint {
          color: var(--theme-text-tertiary, #6A6A6A);
          font-size: 13px;
          line-height: 1.4;
          margin-top: var(--theme-space-1, 4px);
          margin-left: calc(${dotSize} + var(--theme-space-2, 8px));
        }

        @media (pointer: coarse) {
          .row {
            min-height: 44px;
          }
        }
      </style>

      <div part="row" class="row" id="radio-row">
        <div part="dot" class="dot${this._checked ? ' selected' : ''}" id="radio-dot">
          ${
            this._checked
              ? `<div part="dot-inner" class="dot-inner"></div>`
              : ''
          }
        </div>
        ${
          labelText || useSlotForLabel
            ? `<span part="label" class="label" id="radio-label">${labelText}${
                useSlotForLabel ? '<slot></slot>' : ''
              }</span>`
            : ''
        }
      </div>
      ${
        this._hint
          ? `<div part="hint" class="hint" id="radio-hint">${this._escapeHtml(this._hint)}</div>`
          : ''
      }
    `;

    // ARIA on the host (role="radio" with aria-checked on the host element).
    this.setAttribute('role', 'radio');
    this.setAttribute('aria-checked', this._checked ? 'true' : 'false');
    this.setAttribute('tabindex', effectiveDisabled ? '-1' : tabIndex);

    if (effectiveDisabled) {
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('aria-disabled');
      // Only remove the disabled attribute if the group didn't set it.
      if (!groupDisabled) {
        this.removeAttribute('disabled');
      }
    }

    if (blocked) {
      this.setAttribute('data-blocked', '');
    } else {
      this.removeAttribute('data-blocked');
    }

    // Wire up interaction — only when not blocked.
    if (!blocked) {
      this._clickHandler = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const grp = this.closest('qds-radio-group');
        if (grp) {
          (grp as QdsRadioGroup)._selectRadio(this._value);
        }
      };

      this._keydownHandler = (e: KeyboardEvent): void => {
        const grp = this.closest('qds-radio-group');
        if (!grp) return;
        (grp as QdsRadioGroup)._handleRadioKeydown(e, this);
      };

      this.addEventListener('click', this._clickHandler);
      this.addEventListener('keydown', this._keydownHandler);
    }
  }
}

if (!customElements.get('qds-radio')) {
  customElements.define('qds-radio', QdsRadio);
}

// ── QdsRadioGroup ──────────────────────────────────────────────────────

export class QdsRadioGroup extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  static get observedAttributes(): string[] {
    return [
      'value',
      'label',
      'hint',
      'error-text',
      'name',
      'orientation',
      'size',
      'disabled',
      'readonly',
      'required',
      'invalid',
    ];
  }

  private _internals?: ElementInternals;
  private _validity: ValidityTracker;

  private _value: string = '';
  private _options: QdsOption[] = [];
  private _label: string = '';
  private _hint: string = '';
  private _errorText: string = '';
  private _name: string = '';
  private _orientation: string = 'vertical';
  private _size: QdsControlSize = 'md';
  private _disabled: boolean = false;
  private _readonly: boolean = false;
  private _required: boolean = false;
  private _invalid: boolean = false;

  /** Initial value for formResetCallback. */
  private _initialValue: string = '';

  private _labelId: string;
  private _hintId: string;
  private _errorId: string;

  private _slotChangeHandler: ((e: Event) => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    const internals = attachInternalsSafe(this);
    // jsdom's attachInternals returns an object but does not implement
    // setFormValue or validity. Pass undefined so ValidityTracker uses its
    // QdsValidity fallback instead of crashing on undefined validity.
    if (internals && typeof internals.setFormValue === 'function') {
      this._internals = internals;
    }
    this._validity = new ValidityTracker(this._internals);
    this._labelId = generateControlId('radiogroup-label');
    this._hintId = generateControlId('radiogroup-hint');
    this._errorId = generateControlId('radiogroup-error');
    loadBaseCSS();
  }

  // ── Properties ──────────────────────────────────────────────────────

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    const val = String(value);
    if (this._value === val) return;
    this._value = val;
    this.setAttribute('value', val);
    this._updateFormValue();
    this._updateValidity();
    this._updateRadioCheckedStates();
    this._updateRadioTabindex();
    this.render();
  }

  get options(): QdsOption[] {
    return this._options;
  }

  set options(value: QdsOption[]) {
    this._options = Array.isArray(value) ? value : [];
    if (this.isConnected) this.render();
  }

  get label(): string {
    return this._label;
  }

  set label(value: string) {
    this._label = String(value);
    this.setAttribute('label', this._label);
    if (this.isConnected) this.render();
  }

  get hint(): string {
    return this._hint;
  }

  set hint(value: string) {
    this._hint = String(value);
    this.setAttribute('hint', this._hint);
    if (this.isConnected) this.render();
  }

  get errorText(): string {
    return this._errorText;
  }

  set errorText(value: string) {
    this._errorText = String(value);
    this.setAttribute('error-text', this._errorText);
    if (this.isConnected) this.render();
  }

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this._name = String(value);
    this.setAttribute('name', this._name);
  }

  get orientation(): string {
    return this._orientation;
  }

  set orientation(value: string) {
    this._orientation = value === 'horizontal' ? 'horizontal' : 'vertical';
    this.setAttribute('orientation', this._orientation);
    if (this.isConnected) this.render();
  }

  get size(): QdsControlSize {
    return this._size;
  }

  set size(value: QdsControlSize) {
    this._size = parseSize(value);
    this.setAttribute('size', this._size);
    if (this.isConnected) this.render();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = Boolean(value);
    reflectBoolean(this, 'disabled', this._disabled);
    if (this.isConnected) {
      this._updateRadioTabindex();
      this.render();
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    reflectBoolean(this, 'readonly', this._readonly);
    if (this.isConnected) {
      this._updateRadioTabindex();
      this.render();
    }
  }

  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = Boolean(value);
    reflectBoolean(this, 'required', this._required);
    this._updateValidity();
    if (this.isConnected) this.render();
  }

  get invalid(): boolean {
    return this._invalid;
  }

  set invalid(value: boolean) {
    this._invalid = Boolean(value);
    reflectBoolean(this, 'invalid', this._invalid);
    if (this.isConnected) this.render();
  }

  get validity(): QdsValidity {
    return this._validity.validity;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;

    upgradeProperty(this, 'options');
    this._value = this.getAttribute('value') || '';
    this._label = this.getAttribute('label') || '';
    this._hint = this.getAttribute('hint') || '';
    this._errorText = this.getAttribute('error-text') || '';
    this._name = this.getAttribute('name') || '';
    this._orientation = this.getAttribute('orientation') === 'horizontal' ? 'horizontal' : 'vertical';
    this._size = parseSize(this.getAttribute('size'));
    this._disabled = isAttrTruthy(this.getAttribute('disabled'));
    this._readonly = isAttrTruthy(this.getAttribute('readonly'));
    this._required = isAttrTruthy(this.getAttribute('required'));
    this._invalid = isAttrTruthy(this.getAttribute('invalid'));

    this._initialValue = this._value;

    this._updateFormValue();
    this._updateValidity();
    this.render();
  }

  disconnectedCallback(): void {
    if (this._slotChangeHandler) {
      const slot = this.shadowRoot?.querySelector('slot');
      if (slot) {
        slot.removeEventListener('slotchange', this._slotChangeHandler);
      }
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value':
        this._value = newValue || '';
        this._updateFormValue();
        this._updateValidity();
        break;
      case 'label':
        this._label = newValue || '';
        break;
      case 'hint':
        this._hint = newValue || '';
        break;
      case 'error-text':
        this._errorText = newValue || '';
        break;
      case 'name':
        this._name = newValue || '';
        break;
      case 'orientation':
        this._orientation = newValue === 'horizontal' ? 'horizontal' : 'vertical';
        break;
      case 'size':
        this._size = parseSize(newValue);
        break;
      case 'disabled':
        this._disabled = newValue !== null && newValue !== 'false';
        break;
      case 'readonly':
        this._readonly = newValue !== null && newValue !== 'false';
        break;
      case 'required':
        this._required = newValue !== null && newValue !== 'false';
        this._updateValidity();
        break;
      case 'invalid':
        this._invalid = newValue !== null && newValue !== 'false';
        break;
    }

    if (this.isConnected) this.render();
  }

  // ── Form association ────────────────────────────────────────────────

  private _updateFormValue(): void {
    if (this._internals && typeof this._internals.setFormValue === 'function') {
      try {
        this._internals.setFormValue(this._value || null);
      } catch {
        /* noop — jsdom does not implement setFormValue */
      }
    }
  }

  private _updateValidity(): void {
    const wasValid = this._validity.valid;

    if (this._required && !this._value) {
      this._validity.setValidity(
        { valueMissing: true, valid: false },
        'Please select an option.',
      );
    } else {
      this._validity.setValidity({ valueMissing: false, valid: true }, '');
    }

    if (wasValid !== this._validity.valid) {
      dispatchValidityChange(this, this._validity);
    }
  }

  checkValidity(): boolean {
    return this._validity.checkValidity();
  }

  reportValidity(): boolean {
    return this._validity.reportValidity();
  }

  setCustomValidity(message: string): void {
    const wasValid = this._validity.valid;
    this._validity.setCustomValidity(message);
    if (wasValid !== this._validity.valid) {
      dispatchValidityChange(this, this._validity);
    }
  }

  formResetCallback(): void {
    this._value = this._initialValue;
    this.setAttribute('value', this._value);
    this._updateFormValue();
    this._updateValidity();
    this._updateRadioCheckedStates();
    this._updateRadioTabindex();
    this.render();
  }

  formDisabledCallback(disabled: boolean): void {
    this.disabled = disabled;
  }

  // ── Radio management ─────────────────────────────────────────────────

  /** Returns the radios that are currently rendered (slotted or property-driven). */
  private _getRadios(): QdsRadio[] {
    if (this._options.length > 0) {
      // Property options render internal radios — query shadow DOM.
      return Array.from(
        this.shadowRoot?.querySelectorAll('qds-radio') || [],
      ) as QdsRadio[];
    }
    return Array.from(this.querySelectorAll('qds-radio')) as QdsRadio[];
  }

  /** Returns enabled radios (skipping disabled ones). */
  private _getEnabledRadios(): QdsRadio[] {
    return this._getRadios().filter((r) => !r.disabled);
  }

  private _updateRadioCheckedStates(): void {
    const radios = this._getRadios();
    for (const radio of radios) {
      radio.checked = radio.value === this._value;
    }
  }

  private _updateRadioTabindex(): void {
    const radios = this._getEnabledRadios();
    if (radios.length === 0) return;

    let foundSelected = false;
    for (const radio of radios) {
      if (radio.checked) {
        radio.setAttribute('tabindex', '0');
        foundSelected = true;
      } else {
        radio.setAttribute('tabindex', '-1');
      }
    }

    // When nothing is selected, the first enabled radio takes tabindex=0.
    if (!foundSelected) {
      radios[0].setAttribute('tabindex', '0');
    }
  }

  /** Selects a radio by value and fires change/input. Called by child radios. */
  _selectRadio(value: string): void {
    if (this._disabled || this._readonly) return;
    if (value === this._value) return;

    this._value = value;
    this.setAttribute('value', value);
    this._updateFormValue();
    this._updateValidity();
    this._updateRadioCheckedStates();
    this._updateRadioTabindex();

    const detail: RadioGroupChangeEventDetail = {
      component: this,
      value: this._value,
    };

    this.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail,
    }));

    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      composed: true,
      detail,
    }));
  }

  /** Handles keydown on a child radio for ARIA radio-group keyboard nav. */
  _handleRadioKeydown(e: KeyboardEvent, radio: QdsRadio): void {
    if (this._disabled || this._readonly) return;

    const enabled = this._getEnabledRadios();
    if (enabled.length === 0) return;

    const currentIndex = enabled.indexOf(radio);
    if (currentIndex === -1) return;

    let targetIndex: number | null = null;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        targetIndex = (currentIndex + 1) % enabled.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        targetIndex = (currentIndex - 1 + enabled.length) % enabled.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = enabled.length - 1;
        break;
      case ' ':
        e.preventDefault();
        if (!radio.checked) {
          this._selectRadio(radio.value);
        }
        return;
      default:
        return;
    }

    if (targetIndex !== null) {
      e.preventDefault();
      const target = enabled[targetIndex];
      target.focus();
      this._selectRadio(target.value);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private render(): void {
    if (!this.shadowRoot) return;

    const showLabel = this._label !== '';
    const showRequired = this._required;
    const showError = this._invalid && this._errorText !== '';
    const showHint = this._hint !== '' && !showError;

    const describeBy: string[] = [];
    if (this._hint) describeBy.push(this._hintId);
    if (this._invalid && this._errorText) describeBy.push(this._errorId);

    const isHorizontal = this._orientation === 'horizontal';

    // Build internal radios from property options (if any).
    let optionsHtml = '';
    if (this._options.length > 0) {
      for (const opt of this._options) {
        const checked = opt.value === this._value ? ' checked' : '';
        const disabled = opt.disabled ? ' disabled' : '';
        const labelAttr = opt.label ? ` label="${this._escapeHtml(opt.label)}"` : '';
        const hintAttr = opt.description ? ` hint="${this._escapeHtml(opt.description)}"` : '';
        optionsHtml += `<qds-radio value="${this._escapeHtml(opt.value)}"${labelAttr}${hintAttr}${checked}${disabled}></qds-radio>`;
      }
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333);
          font-family: inherit;
        }

        :host([disabled]) {
          opacity: 0.55;
          pointer-events: none;
        }

        .container {
          display: flex;
          flex-direction: column;
          gap: var(--theme-space-1, 4px);
        }

        .label {
          font-size: 14px;
          color: var(--theme-text-secondary, #555);
          font-weight: 500;
          line-height: 1.4;
        }

        :host([size="sm"]) .label {
          font-size: 13px;
        }

        .required {
          color: var(--theme-error, #8B1F00);
          margin-left: 2px;
        }

        .options {
          display: flex;
          flex-direction: ${isHorizontal ? 'row' : 'column'};
          gap: var(--theme-space-${isHorizontal ? '4' : '2'}, ${isHorizontal ? '16px' : '8px'});
          flex-wrap: wrap;
        }

        .hint {
          font-size: 13px;
          color: var(--theme-text-tertiary, #6A6A6A);
          line-height: 1.4;
          margin-top: var(--theme-space-1, 4px);
        }

        .error {
          font-size: 13px;
          color: var(--theme-error, #8B1F00);
          line-height: 1.4;
          margin-top: var(--theme-space-1, 4px);
        }
      </style>

      <div part="container" class="container">
        ${
          showLabel
            ? `<div part="label" class="label" id="${this._labelId}">${this._escapeHtml(this._label)}${
                showRequired
                  ? ' <span part="required" class="required" aria-hidden="true">*</span>'
                  : ''
              }</div>`
            : ''
        }
        <div part="options" class="options" id="radio-options">
          ${
            this._options.length > 0
              ? optionsHtml
              : '<slot></slot>'
          }
        </div>
        ${
          showHint
            ? `<div part="hint" class="hint" id="${this._hintId}">${this._escapeHtml(this._hint)}</div>`
            : ''
        }
        ${
          showError
            ? `<div part="error" class="error" id="${this._errorId}">${this._escapeHtml(this._errorText)}</div>`
            : ''
        }
      </div>
    `;

    // Wire up ARIA on the host.
    this.setAttribute('role', 'radiogroup');

    if (showLabel) {
      this.setAttribute('aria-labelledby', this._labelId);
    } else {
      this.removeAttribute('aria-labelledby');
    }

    if (describeBy.length > 0) {
      this.setAttribute('aria-describedby', describeBy.join(' '));
    } else {
      this.removeAttribute('aria-describedby');
    }

    if (this._required) {
      this.setAttribute('aria-required', 'true');
    } else {
      this.removeAttribute('aria-required');
    }

    if (this._invalid) {
      this.setAttribute('aria-invalid', 'true');
    } else {
      this.removeAttribute('aria-invalid');
    }

    if (this._disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }

    // Attach slotchange for slotted radios.
    if (this._options.length === 0) {
      if (!this._slotChangeHandler) {
        this._slotChangeHandler = (): void => {
          this._updateRadioCheckedStates();
          this._updateRadioTabindex();
        };
      }
      const slot = this.shadowRoot.querySelector('slot');
      if (slot) {
        slot.addEventListener('slotchange', this._slotChangeHandler);
      }
    }

    // After render, sync radio states and tabindex.
    // Defer to next microtask so child radios are connected first.
    Promise.resolve().then(() => {
      if (!this.isConnected) return;
      this._updateRadioCheckedStates();
      this._updateRadioTabindex();
    });
  }
}

if (!customElements.get('qds-radio-group')) {
  customElements.define('qds-radio-group', QdsRadioGroup);
}
